<!--
	SafeCurves dashboard: Identify and Prioritize donut breakdowns (by length) plus the
	Interventions sign chart.
-->
<script lang="ts">
	import { ESafeCurvesModule, type ISafeCurvesSummaryData } from '../interfaces';
	import { mapPriorityDescription } from '../prioritize';
	import InterventionsBarChart from './InterventionsBarChart.svelte';
	import LegendPatch from '#lib/components/LegendPatch.svelte';
	import SummaryCard, { tally } from '../../SummaryCard.svelte';
	import SummaryCardGroup from '../../SummaryCardGroup.svelte';
	import { identifyColours, prioritizeColours, interventionsColours } from '#lib/vizPalette.js';
	import { jurisdictionStore } from '#lib/stores/jurisdictionStore.svelte.js';
	import { convert, getUnitLabel } from '#lib/intlUnits.js';
	import { unitSystemStore } from '#lib/stores/unitSystemStore.svelte.js';

	interface Props {
		summaryData: ISafeCurvesSummaryData;
	}
	const { summaryData }: Props = $props();

	const interventionsOrderByJurisdiction = {
		USA: ['W1-1', 'W1-2', 'W1-3', 'W1-4', 'W1-5', 'W1-11'],
		AUS: ['W1-1', 'W1-3', 'W1-2', 'W1-4', 'W1-5', 'W1-7']
	} as const;
	const interventionsOrder = $derived(
		interventionsOrderByJurisdiction[jurisdictionStore.jurisdiction]
	);

	const identifyRows = $derived(summaryData[ESafeCurvesModule.Identify]);
	const prioritizeRows = $derived(summaryData[ESafeCurvesModule.Prioritize]);
	const identifyLabel = (r: Record<string, string | number>) => r.curve_context as string;
	const prioritizeLabel = (r: Record<string, string | number>) =>
		mapPriorityDescription[r.priority_rating as string];

	const interventionsPlotData = $derived.by(() => {
		const bars = new Map(
			interventionsOrder.map((name) => [name, { name, Required: 0, Recommended: 0, Optional: 0 }])
		);
		for (const row of summaryData[ESafeCurvesModule.Interventions] ?? []) {
			// Server data: skip rows without a recognised "(W1-x)" sign code or recommendation level.
			const bar = bars.get(/\(([^)]+)\)/.exec(String(row.advisory_sign))?.[1] as never);
			const rec = row.advisory_sign_recommendation as 'Required' | 'Recommended' | 'Optional';
			if (bar && ['Required', 'Recommended', 'Optional'].includes(rec)) {
				bar[rec] += row.count as number;
			}
		}
		return [...bars.values()];
	});

	const SIG3 = { maximumSignificantDigits: 3 } as const;

	/** Count and length headline for `key`, out of the totals across all keys. */
	function statsOf(
		rows: Record<string, string | number>[] | undefined,
		labelOf: (r: Record<string, string | number>) => string,
		colours: { key: string; color: string }[],
		key: string,
		noun: string
	) {
		const [count, length] = ['count', 'total_length_km'].map((field) => {
			const slices = tally(rows ?? [], labelOf, colours, field);
			return {
				value: slices.find((s) => s.key === key)!.value,
				total: slices.reduce((a, s) => a + s.value, 0)
			};
		});
		const system = unitSystemStore.systemFor('length');
		const toLength = (km: number) =>
			convert(km, 'bigLength', 'metric', system).toLocaleString('en-US', SIG3);
		return [
			{
				value: count.value.toLocaleString('en-US'),
				of: `of ${count.total.toLocaleString('en-US')} ${noun}s`
			},
			{
				value: toLength(length.value),
				unit: getUnitLabel('bigLength', system, 'short'),
				of: `of ${toLength(length.total)} ${noun} ${getUnitLabel('bigLength', system, 'long')}`
			}
		];
	}
</script>

<SummaryCardGroup>
	<SummaryCard
		title="Identify"
		blurb="Horizontal curves categorized by crash risk."
		available={!!identifyRows}
		statHeading="Class 1 curves"
		stats={statsOf(identifyRows, identifyLabel, identifyColours, 'Class 1', 'curve')}
		donutHeading="Curve class breakdown by length"
		donut={tally(identifyRows ?? [], identifyLabel, identifyColours, 'total_length_km')} />
	<SummaryCard
		title="Prioritize"
		blurb="Curve risk aggregated into prioritized corridors."
		available={!!prioritizeRows}
		statHeading="Priority 1 corridors"
		stats={statsOf(prioritizeRows, prioritizeLabel, prioritizeColours, 'Priority 1', 'corridor')}
		donutHeading="Corridor priority breakdown by length"
		donut={tally(prioritizeRows ?? [], prioritizeLabel, prioritizeColours, 'total_length_km')} />
	<SummaryCard
		title="Interventions"
		blurb="Reduce the likelihood of crashes with appropriate interventions."
		available={!!summaryData[ESafeCurvesModule.Interventions]}>
		<h3>Horizontal alignment sign requirements</h3>
		<div class="barChartContainer">
			<InterventionsBarChart
				data={interventionsPlotData}
				colours={interventionsColours}
				width={348}
				height={286} />
		</div>
		<ul class="chartKey">
			{#each interventionsColours as item (item.key)}
				<li><LegendPatch color={item.color} shape="square" size="sm" />{item.key}</li>
			{/each}
		</ul>
	</SummaryCard>
</SummaryCardGroup>

<style>
	.barChartContainer {
		margin: 0 auto;
		max-width: 21rem;
	}

	.chartKey {
		display: flex;
		justify-content: center;
		gap: var(--space-5);
		margin: 0;
		padding: 0;
		list-style: none;

		li {
			display: flex;
			align-items: center;
			gap: var(--space-2);
			font-size: var(--font-size-sm);
		}
	}
</style>
